import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, PlusIcon } from '@itsrever/design-system'
import {
  ReturnPeriodRule,
  matchingReturnPeriodRule,
  newReturnPeriodRule,
  overlappedBy,
  returnPeriodRuleComplete,
  sameReturnPeriodRule
} from '@/domain/return-conditions'
import {
  OpenKey,
  RuleListFrame,
  RuleRow,
  newRuleKey,
  ruleStatus,
  savedKey,
  useRuleOrder
} from '@/ui/components/rule-list'
import { DeadlineCell } from './deadline-cell'
import { PurchaseRangeChip } from './purchase-range-chip'
import { NS, formatDay } from './utils'

const { Button, FieldError, FieldLabel, FieldRoot, Input } = Base

interface Keyed {
  key: string
  rule: ReturnPeriodRule
}

// The return window: a deadline per purchase date range, and the days for any other purchase date.
export const ReturnWindowField: React.FC<{
  rules: ReturnPeriodRule[]
  saved: ReturnPeriodRule[]
  days: number
  daysError?: string
  editable: boolean
  onChange: (rules: ReturnPeriodRule[]) => void
  onDaysChange: (days: number) => void
}> = ({ rules, saved, days, daysError, editable, onChange, onDaysChange }) => {
  const { t, i18n } = useTranslation()
  const [openKey, setOpenKey] = useState<OpenKey>(null)
  const [checkDate, setCheckDate] = useState('')
  const checkId = useId()
  // Rows are keyed here, as in RuleTable: the stored rules carry no id.
  const [order, setOrder] = useState<{
    saved: ReturnPeriodRule[]
    keys: string[]
  }>({ saved, keys: [] })
  const fresh =
    rules === saved ||
    order.saved !== saved ||
    order.keys.length !== rules.length
  const keyed: Keyed[] = rules.map((rule, i) => ({
    rule,
    key: fresh ? savedKey(i) : order.keys[i]
  }))
  const setRules = (next: Keyed[]) => {
    setOrder({ saved, keys: next.map((row) => row.key) })
    onChange(next.map((row) => row.rule))
  }
  const rowProps = useRuleOrder(keyed, setRules)
  const update = (key: string) => (rule: ReturnPeriodRule) =>
    setRules(keyed.map((row) => (row.key === key ? { key, rule } : row)))
  const addRule = () => {
    const key = newRuleKey()
    setRules([...keyed, { key, rule: newReturnPeriodRule() }])
    setOpenKey(`${key}:range`)
  }

  const matched = checkDate ? matchingReturnPeriodRule(rules, checkDate) : -1
  const daysLabel = (count: number) => t(`${NS}.days`, { count })

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm text-neutral-900">
        {t(`${NS}.title`)}
        <span aria-hidden="true" className="text-red-500">
          {' '}
          *
        </span>
      </p>
      <p className="text-xs text-neutral-600">{t(`${NS}.description`)}</p>
      <RuleListFrame
        ns={NS}
        outcome={{
          label: t(`${NS}.column_deadline`),
          className: 'w-44 text-right'
        }}
        fallbackHighlighted={!!checkDate && matched < 0}
        fallback={
          editable ? (
            <span className="flex w-44 shrink-0 items-center justify-end gap-1.5 px-2">
              <Input
                size="sm"
                type="number"
                min={1}
                className="w-16"
                aria-label={t(`${NS}.fallback_label`)}
                aria-invalid={!!daysError}
                value={days}
                onChange={(e) => onDaysChange(Number(e.currentTarget.value))}
              />
              <span className="text-sm text-neutral-600">
                {t(`${NS}.days_unit`)}
              </span>
            </span>
          ) : (
            <span className="w-44 shrink-0 px-2 text-right text-sm font-medium tabular-nums text-neutral-900">
              {daysLabel(days)}
            </span>
          )
        }
      >
        {keyed.map((row, index) => {
          const overlap = overlappedBy(rules, index)
          const cell = {
            id: row.key,
            rule: row.rule,
            n: index + 1,
            editable,
            openKey,
            setOpenKey,
            onChange: update(row.key)
          }
          return (
            <RuleRow
              key={row.key}
              {...rowProps(row, index)}
              ns={NS}
              status={ruleStatus(row, saved, (r, original) =>
                sameReturnPeriodRule(r.rule, original)
              )}
              highlighted={!!checkDate && index === matched}
              dimmed={!!checkDate && matched >= 0 && index > matched}
              editable={editable}
              outcome={<DeadlineCell {...cell} />}
              onDelete={() => setRules(keyed.filter((r) => r.key !== row.key))}
            >
              <PurchaseRangeChip {...cell} />
              {overlap >= 0 ? (
                <p className="w-full text-xs text-amber-700">
                  {t(`${NS}.overlaps`, { n: overlap + 1 })}
                </p>
              ) : null}
            </RuleRow>
          )
        })}
      </RuleListFrame>
      {daysError ? (
        <FieldRoot invalid>
          <FieldError match>{daysError}</FieldError>
        </FieldRoot>
      ) : null}
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-2">
        {editable ? (
          <Button type="button" variant="ghost" size="sm" onClick={addRule}>
            <PlusIcon color="currentColor" className="size-4" />
            {t(`${NS}.add_rule`)}
          </Button>
        ) : null}
        <FieldRoot className="ml-auto flex-row items-center gap-2">
          <FieldLabel htmlFor={checkId} className="text-xs text-neutral-500">
            {t(`${NS}.check_date`)}
          </FieldLabel>
          <Input
            id={checkId}
            type="date"
            size="sm"
            className="w-40"
            value={checkDate}
            onChange={(event) => setCheckDate(event.target.value)}
          />
        </FieldRoot>
      </div>
      {checkDate ? (
        <p className="text-right text-xs text-neutral-700">
          {matched < 0
            ? t(`${NS}.check_fallback`, { days: daysLabel(days) })
            : !returnPeriodRuleComplete(rules[matched])
              ? t(`${NS}.check_incomplete`, { n: matched + 1 })
              : rules[matched].deadline.kind === 'until'
                ? t(`${NS}.check_until`, {
                    date: formatDay(
                      rules[matched].deadline.date,
                      i18n.language
                    ),
                    n: matched + 1
                  })
                : t(`${NS}.check_days`, {
                    days: daysLabel(rules[matched].deadline.days ?? 0),
                    n: matched + 1
                  })}
        </p>
      ) : null}
    </div>
  )
}
